// authLoader.tsx
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLoginRedirect } from '@/features/auth/api/loginRedirect'
import { useLoginCallback } from '@/features/auth/api/loginCallback'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '../store/store'
import { authAction } from '../store/actions/authAction'
import { Button, Result } from 'antd'
import { LoadingPage } from '@/pages/loading'
import { paths } from '../routes/path'

type AuthLoaderProps = {
  children: ReactNode
}

const allowedRedirects = [paths.app.root.getHref()]

function sanitizeRedirectUrl(url: string | null) {
  if (!url) return paths.app.root.getHref()
  if (!url.startsWith('/')) {
    return paths.app.root.getHref()
  }
  if (!allowedRedirects.some((p) => url === p || url.startsWith(p))) {
    return paths.app.root.getHref()
  }

  return url
}

const AuthLoader = ({ children }: AuthLoaderProps) => {
  const [isAuthCompleted, setIsAuthCompleted] = useState(false)
  const [authError, setAuthError] = useState<unknown>(null)
  const dispatch = useDispatch<AppDispatch>()
  const isCallbacked = useRef(false)

  const { mutate: redirectMutate } = useLoginRedirect({
    mutationConfig: {
      onSuccess: () => {
        const redirectUrl = location.pathname + location.search
        sessionStorage.setItem('redirectUrl', redirectUrl !== paths.app.root.getHref() ? redirectUrl : paths.app.root.getHref())
      },
      retry: true,
      retryDelay: 5000,
      meta: { silent: true },
    },
  })

  const { mutate: callbackMutate } = useLoginCallback({
    mutationConfig: {
      onSuccess: async (res: any) => {
        const redirectUrl = sanitizeRedirectUrl(sessionStorage.getItem('redirectUrl'))
        sessionStorage.removeItem('redirectUrl')
        window.history.replaceState({}, '', redirectUrl ?? paths.app.root.getHref())
        await dispatch(authAction({ authResponse: res })).unwrap()
        setIsAuthCompleted(true)
      },
      onError: setAuthError,
      meta: { silent: true },
    },
  })

  useEffect(() => {
    if (isCallbacked.current) return
    isCallbacked.current = true

    const searchParams = new URLSearchParams(window.location.search)
    const code = searchParams.get('code')

    if (code) {
      callbackMutate({ code })
    } else {
      redirectMutate()
    }
  }, [callbackMutate, redirectMutate])

  if (isAuthCompleted) {
    return <>{children}</>
  }

  if (authError) {
    console.error('Authentication Error:', authError)
    return (
      <div className="h-dvh flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <Result
            status="warning"
            title={'Oops! Something went wrong. Please try again later or refresh the page.'}
            extra={<Button onClick={() => (window.location.href = paths.app.root.getHref())}>Reload</Button>}
          />
        </div>
      </div>
    )
  }

  return <LoadingPage />
}

export { AuthLoader }
